import { Dispatch, ReactNode, useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { CreateInventoryPositionDetails } from '../../types';
import { formatEther, parseEther } from 'viem';
import VaultIcon from '@/presentation/components/elements/VaultIcon';

const InputVToken = ({
  maxAmount,
  dispatch,
  onSubmit,
  Sidebar,
  symbol,
  vaultId,
}: {
  maxAmount: bigint;
  dispatch: Dispatch<Partial<CreateInventoryPositionDetails>>;
  onSubmit: () => void;
  Sidebar: ReactNode;
  symbol: string;
  vaultId: string;
}) => {
  const { control, handleSubmit } = useForm<{
    vTokenAmount: string;
  }>();

  const { vTokenAmount = '0' } = useWatch({ control });

  useEffect(() => {
    if (vTokenAmount) {
      dispatch({ vTokenAmount });
    }
  }, [dispatch, vTokenAmount]);

  const onFormSubmit = handleSubmit(() => {
    onSubmit();
  });

  return (
    <form
      className="grid grid-cols-1 md:grid-cols-3 gap-8 relative"
      onSubmit={onFormSubmit}
    >
      <div className="md:col-span-2">
        <Controller
          name="vTokenAmount"
          control={control}
          rules={{
            validate: (v) => {
              if (!v) {
                return 'Required';
              }
              const value = parseEther(v);
              if (value > maxAmount) {
                return 'Insufficient funds';
              }
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <label className="space-y-4">
                <div className="flex items-center">
                  <VaultIcon vaultId={vaultId} className="h-8 w-8 mr-4" />
                  <span className="font-medium text-lg">{symbol}</span>
                </div>
                <div className="space-y-2 lg:max-w-md w-full p-4 border border-gray-300 bg-gray-100 rounded block">
                  <div className="flex justify-between">
                    <span className="uppercase text-xs font-medium">
                      {'Enter amount'}
                    </span>
                    <button
                      type="button"
                      onClick={() => field.onChange(formatEther(maxAmount))}
                      className="text-xs"
                    >
                      <span className="uppercase font-medium">{'MAX:'}</span>{' '}
                      <span className="underline hover:no-underline">
                        {formatEther(maxAmount)}
                      </span>
                    </button>
                  </div>
                  <input
                    {...field}
                    className="block border border-gray-300 bg-gray-50 text-gray-800 text-lg text-right p-4 rounded focus:outline-none focus:border-pink-300 w-full placeholder:text-gray-400"
                    placeholder="0.00"
                  />
                </div>
              </label>
              {!!fieldState.error?.message && (
                <div>{fieldState.error.message}</div>
              )}
            </>
          )}
        />
      </div>
      {Sidebar}
    </form>
  );
};

export default InputVToken;
